import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  VStack,
  useToast,
  Textarea,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import axios from "axios";
import { api_url } from "../../../url";
import CustomSwitch from "../CustomSwitch";

export default function AddGameModal({
  isOpen,
  onClose,
  onSuccess,
  providers,
}) {
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    provider: "",
    providerGameId: "",
    gameType: "",
    category: "",
    thumbnail: "",
    description: "",
    status: true,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      // Ensure provider is the ID, not the name, depending on what the backend expects.
      // Schema says provider: Joi.string().required(). Usually an ID.
      // So the Select value should be the provider ID.

      const payload = { ...formData };

      const { data } = await axios.post(`${api_url}/games`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (data.success) {
        toast({
          title: "Game added successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setFormData({
          name: "",
          slug: "",
          provider: "",
          providerGameId: "",
          gameType: "",
          category: "",
          thumbnail: "",
          description: "",
          status: true,
        });
        onSuccess();
        onClose();
      }
    } catch (error) {
      toast({
        title: "Error adding game",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add New Game</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Game Name</FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. Starburst"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Slug</FormLabel>
                <Input
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  placeholder="e.g. starburst"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Provider</FormLabel>
                <Select
                  name="provider"
                  value={formData.provider}
                  onChange={handleChange}
                  placeholder="Select Provider"
                >
                  {providers.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Provider Game ID</FormLabel>
                <Input
                  name="providerGameId"
                  value={formData.providerGameId}
                  onChange={handleChange}
                  placeholder="External Game ID"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Game Type</FormLabel>
                <Select
                  name="gameType"
                  value={formData.gameType}
                  onChange={handleChange}
                  placeholder="Select Type"
                >
                  <option value="slot">Slot</option>
                  <option value="live">Live Casino</option>
                  <option value="table">Table Game</option>
                  <option value="crash">Crash Game</option>
                  <option value="other">Other</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Category</FormLabel>
                <Input
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  placeholder="e.g. Slots"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Thumbnail URL</FormLabel>
                <Input
                  name="thumbnail"
                  value={formData.thumbnail}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Game description"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="game-status-switch" mb="0">
                  Game Status
                </FormLabel>
                <CustomSwitch
                  name="status"
                  isChecked={formData.status}
                  onChange={handleChange}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" type="submit" isLoading={loading}>
              Add Game
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
